// Tiny dictionary-based i18n for the PATIENT-FACING intake pages only
// (intake-submit.tsx: token flow + public flow). Arabic is the default.

export type Lang = 'ar' | 'en';

const LANG_STORAGE_KEY = 'intake-lang';

export function loadIntakeLang(): Lang {
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === 'en' || stored === 'ar') return stored;
  } catch {
    // localStorage unavailable (private mode etc.) — fall through to default
  }
  return 'ar';
}

export function saveIntakeLang(lang: Lang): void {
  try {
    window.localStorage.setItem(LANG_STORAGE_KEY, lang);
  } catch {
    // best-effort persistence only
  }
}

const STRINGS = {
  // ---- chrome / loader ----
  language_toggle: { en: 'العربية', ar: 'English' },
  header_subtitle: { en: 'Referral intake', ar: 'طلب إحالة' },
  form_unavailable_title: { en: 'Form unavailable', ar: 'النموذج غير متاح' },
  form_unavailable_message: { en: 'This form is not available.', ar: 'هذا النموذج غير متاح حاليًا.' },

  // ---- emergency notice ----
  emergency_strong: { en: 'This is not an emergency service.', ar: 'هذه ليست خدمة طوارئ.' },
  emergency_rest: {
    en: 'For urgent or life-threatening symptoms, contact emergency services or visit the nearest emergency department.',
    ar: 'في الحالات العاجلة أو التي تهدد الحياة، يرجى الاتصال بخدمات الطوارئ أو التوجه إلى أقرب قسم طوارئ.',
  },

  // ---- form card header ----
  packet_kicker: { en: 'Referral request', ar: 'طلب إحالة طبية' },
  packet_title: {
    en: 'Medical referral request form',
    ar: 'نموذج طلب إحالة طبية',
  },
  packet_subtitle: {
    en: 'Please complete the details below and attach the required documents.',
    ar: 'يرجى تعبئة البيانات أدناه وإرفاق المستندات المطلوبة.',
  },
  prepared_by_staff: { en: 'Prepared by staff:', ar: 'أُعدّ بواسطة موظفي المستشفى:' },

  // ---- fields ----
  full_name: { en: 'Full name', ar: 'الاسم الكامل' },
  mobile: { en: 'Mobile number', ar: 'رقم الجوال' },
  mobile_placeholder: { en: '05xxxxxxxx', ar: '05xxxxxxxx' },
  identity_type: { en: 'Identity type', ar: 'نوع الهوية' },
  identity_type_national_id: { en: 'National ID', ar: 'الهوية الوطنية' },
  identity_type_iqama: { en: 'Iqama', ar: 'الإقامة' },
  identity_type_passport: { en: 'Passport', ar: 'جواز السفر' },
  identity_type_other: { en: 'Other', ar: 'أخرى' },
  identity_number: { en: 'Identity number', ar: 'رقم الهوية' },
  nationality: { en: 'Nationality', ar: 'الجنسية' },
  referring_provider: { en: 'Referring clinic / hospital', ar: 'الجهة المُحوِّلة (عيادة / مستشفى)' },
  referring_provider_placeholder: { en: 'If applicable', ar: 'إن وُجدت' },
  payment_method: { en: 'Payment method', ar: 'طريقة الدفع' },
  payment_method_placeholder: { en: 'Choose…', ar: 'اختر…' },
  payment_method_cash: { en: 'Cash (self-pay)', ar: 'نقدًا (على حساب المريض)' },
  payment_method_insurance: { en: 'Insurance', ar: 'تأمين' },
  referral_reason: { en: 'Reason for referral', ar: 'سبب الإحالة' },
  referral_reason_placeholder: {
    en: 'Describe the symptoms, diagnosis, requested care, and relevant history',
    ar: 'اذكر الأعراض والتشخيص والرعاية المطلوبة وأي تاريخ طبي ذي صلة',
  },

  // ---- documents ----
  documents_title: { en: 'Documents', ar: 'المستندات' },
  documents_rules: {
    en: 'PDF, JPEG, or PNG · up to 10 files in total · 20 MB per file',
    ar: 'بصيغة PDF أو JPEG أو PNG · بحد أقصى 10 ملفات إجمالًا · 20 ميجابايت لكل ملف',
  },
  doc_identity_title: { en: 'Identity document', ar: 'وثيقة الهوية' },
  doc_identity_hint: { en: 'National ID, Iqama, or passport', ar: 'هوية وطنية أو إقامة أو جواز سفر' },
  doc_medical_title: { en: 'Medical reports', ar: 'التقارير الطبية' },
  doc_medical_hint: { en: 'Reports, referral letters, or images', ar: 'تقارير أو خطابات إحالة أو صور أشعة' },
  doc_no_insurance_title: { en: 'Proof of no insurance', ar: 'إثبات عدم وجود تأمين' },
  doc_no_insurance_hint: {
    en: 'A document confirming you have no insurance coverage',
    ar: 'وثيقة تُثبت عدم وجود تغطية تأمينية',
  },
  doc_other_title: { en: 'Other documents', ar: 'مستندات أخرى' },
  doc_other_hint: { en: 'Anything else that helps your case', ar: 'أي مستندات أخرى تدعم حالتك' },
  add_file: { en: 'Add file', ar: 'إضافة ملف' },
  add_files: { en: 'Add files', ar: 'إضافة ملفات' },
  remove_file: { en: 'Remove', ar: 'إزالة' },

  // ---- document validation ----
  err_too_many_documents: {
    en: 'Choose no more than 10 documents in total across all sections.',
    ar: 'يمكن اختيار 10 مستندات كحد أقصى في جميع الأقسام.',
  },
  err_unsupported_type: {
    en: '{name} is not a PDF, JPEG, or PNG document.',
    ar: 'الملف {name} ليس بصيغة PDF أو JPEG أو PNG.',
  },
  err_too_large: {
    en: '{name} is larger than 20 MB.',
    ar: 'حجم الملف {name} يتجاوز 20 ميجابايت.',
  },
  err_empty_file: {
    en: '{name} is empty.',
    ar: 'الملف {name} فارغ.',
  },

  // ---- consent / submit ----
  consent_text_ar: {
    en: 'I consent to this hospital collecting and reviewing my medical information for the purpose of a referral, and to sharing it with the referral network as needed to process my case.',
    ar: 'أوافق على قيام هذا المستشفى بجمع معلوماتي الطبية ومراجعتها لغرض الإحالة، وعلى مشاركتها مع شبكة الإحالة بالقدر اللازم لمعالجة حالتي.',
  },
  submit: { en: 'Submit referral', ar: 'إرسال الطلب' },
  submitting: { en: 'Submitting…', ar: 'جارٍ الإرسال…' },
  submitting_with_uploads: { en: 'Submitting and uploading…', ar: 'جارٍ الإرسال ورفع المستندات…' },
  err_submit_failed: {
    en: 'Could not submit. Please try again.',
    ar: 'تعذّر إرسال الطلب. يرجى المحاولة مرة أخرى.',
  },
  err_recaptcha_load: {
    en: 'Could not load anti-spam verification.',
    ar: 'تعذّر تحميل خاصية التحقق من الحماية.',
  },
  secure_footer: { en: 'Securely processed by RefDash', ar: 'تُعالَج بياناتك بأمان بواسطة RefDash' },

  // ---- done / status screen ----
  done_title: { en: 'Referral received', ar: 'تم استلام طلب الإحالة' },
  done_keep_reference: {
    en: 'Keep this private reference number to check the case later.',
    ar: 'احتفظ بهذا الرقم المرجعي الخاص لمتابعة حالة الطلب لاحقًا.',
  },
  done_uploaded_one: { en: '1 document uploaded securely.', ar: 'تم رفع مستند واحد بأمان.' },
  done_uploaded_many: { en: '{count} documents uploaded securely.', ar: 'تم رفع {count} من المستندات بأمان.' },
  done_upload_errors_one: {
    en: 'The referral was received, but 1 document could not be uploaded. The hospital can request it later.',
    ar: 'تم استلام الطلب، لكن تعذّر رفع مستند واحد. يمكن للمستشفى طلبه منك لاحقًا.',
  },
  done_upload_errors_many: {
    en: 'The referral was received, but {count} documents could not be uploaded. The hospital can request them later.',
    ar: 'تم استلام الطلب، لكن تعذّر رفع {count} من المستندات. يمكن للمستشفى طلبها منك لاحقًا.',
  },
  stage_received: { en: 'Received', ar: 'تم الاستلام' },
  stage_in_review: { en: 'Clinical review', ar: 'المراجعة الطبية' },
  stage_approved: { en: 'Accepted', ar: 'تم القبول' },
  stage_declined: { en: 'Not accepted', ar: 'لم يتم القبول' },
  refresh_status: { en: 'Refresh status', ar: 'تحديث الحالة' },
  checking_status: { en: 'Checking…', ar: 'جارٍ التحقق…' },

  // ---- staff add-case dialog (same dictionary, staff-only keys) ----
  staff_referral_type: { en: 'Referral type', ar: 'نوع الإحالة' },
  staff_referral_type_placeholder: { en: 'Consultation, admission, transfer…', ar: 'استشارة، تنويم، نقل…' },
  staff_requested_specialty: { en: 'Requested specialty', ar: 'التخصص المطلوب' },
  staff_specialty_placeholder: {
    en: 'Search GlobeMed specialties…',
    ar: 'ابحث في تخصصات جلوب ميد…',
  },
  staff_bed_type: { en: 'Requested bed type', ar: 'نوع السرير المطلوب' },
  staff_internal_department: { en: 'Internal department', ar: 'القسم الداخلي' },
  staff_leave_for_triage: { en: 'Leave for triage', ar: 'ترك للفرز' },
  staff_priority: { en: 'Priority', ar: 'الأولوية' },
  staff_priority_routine: { en: 'Routine', ar: 'اعتيادية' },
  staff_priority_urgent: { en: 'Urgent', ar: 'عاجلة' },
  staff_priority_emergency: { en: 'Emergency', ar: 'طارئة' },
  staff_consent: {
    en: 'Consent attestation required. I confirm the patient authorized collection, clinical review, and necessary referral-network sharing of this information.',
    ar: 'إقرار بالموافقة. أُقرّ بأن المريض قد فوّض بجمع هذه المعلومات ومراجعتها سريريًا ومشاركتها مع شبكة الإحالة بالقدر اللازم.',
  },
  staff_create_case: { en: 'Create case', ar: 'إنشاء الحالة' },
  staff_creating_case: { en: 'Creating case…', ar: 'جارٍ إنشاء الحالة…' },
  staff_invite_expires: { en: 'Link expires after', ar: 'تنتهي صلاحية الرابط بعد' },
  staff_ttl_24: { en: '24 hours', ar: '24 ساعة' },
  staff_ttl_72: { en: '3 days', ar: '3 أيام' },
  staff_ttl_168: { en: '7 days', ar: '7 أيام' },
  staff_generate_link: { en: 'Generate private link', ar: 'إنشاء رابط خاص' },
  staff_generating_link: { en: 'Generating…', ar: 'جارٍ الإنشاء…' },
  staff_invite_needs_prefill: {
    en: 'Add at least one patient or clinical detail before generating a private invitation.',
    ar: 'أضف معلومة واحدة على الأقل عن المريض أو الحالة قبل إنشاء رابط الدعوة.',
  },
  staff_copy: { en: 'Copy', ar: 'نسخ' },
  staff_copied: { en: 'Copied', ar: 'تم النسخ' },
} as const;

export type IntakeStringKey = keyof typeof STRINGS;

export function t(lang: Lang, key: IntakeStringKey, vars?: Record<string, string | number>): string {
  let text: string = STRINGS[key][lang];
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      text = text.replaceAll(`{${name}}`, String(value));
    }
  }
  return text;
}
